import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  HardDrive, 
  Cpu, 
  Activity, 
  AlertTriangle, 
  Plus, 
  Trash2, 
  Edit, 
  Save,
  Bell,
  Mail,
  Ticket,
  Server,
  Clock
} from 'lucide-react';

const AssetAlertsSettings = () => {
  const [alertRules, setAlertRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [generalSettings, setGeneralSettings] = useState({
    alert_frequency_minutes: 15,
    create_tickets: true,
    send_emails: true,
    show_dashboard: true
  });

  const [newRule, setNewRule] = useState({
    name: '',
    alert_type: 'disk_space',
    threshold_value: '',
    threshold_unit: '%',
    duration_minutes: 0,
    severity: 'warning',
    enabled: true,
    service_name: ''
  });

  const alertTypes = [
    { value: 'disk_space', label: 'Disk Space Low', icon: HardDrive, units: ['%', 'GB'] },
    { value: 'cpu_usage', label: 'CPU Usage High', icon: Cpu, units: ['%'] },
    { value: 'ram_usage', label: 'RAM Usage High', icon: Activity, units: ['%'] },
    { value: 'service_stopped', label: 'Service Stopped', icon: Server, units: [] }
  ];

  const commonServices = [
    'Spooler', // Print Spooler
    'Dns',
    'W32Time', // Windows Time
    'wuauserv', // Windows Update
    'MSSQLSERVER',
    'SQLSERVERAGENT'
  ];

  const severityColors = {
    info: 'bg-blue-100 text-blue-700 border-blue-200',
    warning: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    critical: 'bg-red-100 text-red-700 border-red-200'
  };

  useEffect(() => {
    fetchAlertRules();
    fetchGeneralSettings();
  }, []);

  const fetchAlertRules = async () => {
    try {
      const response = await api.get('/admin/alert-rules');
      setAlertRules(response.data || []);
    } catch (error) {
      toast.error('Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralSettings = async () => {
    try {
      const response = await api.get('/admin/alert-settings');
      if (response.data) {
        setGeneralSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load alert settings');
    }
  };

  const saveGeneralSettings = async () => {
    try {
      await api.post('/admin/alert-settings', generalSettings);
      toast.success('Alert settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleCreateRule = async () => {
    try {
      if (!newRule.name || !newRule.threshold_value) {
        toast.error('Please fill in all required fields');
        return;
      }

      await api.post('/admin/alert-rules', newRule);
      toast.success('Alert rule created');
      setShowAddDialog(false);
      fetchAlertRules();
      resetNewRule();
    } catch (error) {
      toast.error('Failed to create alert rule');
    }
  };

  const handleUpdateRule = async () => {
    try {
      await api.put(`/admin/alert-rules/${editingRule.id}`, editingRule);
      toast.success('Alert rule updated');
      setEditingRule(null);
      fetchAlertRules();
    } catch (error) {
      toast.error('Failed to update alert rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this alert rule?')) return;
    
    try {
      await api.delete(`/admin/alert-rules/${ruleId}`);
      toast.success('Alert rule deleted');
      fetchAlertRules();
    } catch (error) {
      toast.error('Failed to delete alert rule');
    }
  };

  const toggleRuleEnabled = async (rule) => {
    try {
      await api.put(`/admin/alert-rules/${rule.id}`, {
        ...rule,
        enabled: !rule.enabled
      });
      fetchAlertRules();
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const resetNewRule = () => {
    setNewRule({
      name: '',
      alert_type: 'disk_space',
      threshold_value: '',
      threshold_unit: '%',
      duration_minutes: 0,
      severity: 'warning',
      enabled: true,
      service_name: ''
    });
  };

  const getAlertTypeInfo = (type) => {
    return alertTypes.find(t => t.value === type) || alertTypes[0];
  };

  const renderRuleCard = (rule) => {
    const typeInfo = getAlertTypeInfo(rule.alert_type);
    const Icon = typeInfo.icon;

    return (
      <Card key={rule.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="font-semibold text-slate-800 break-words">{rule.name}</h3>
                  <Badge className={severityColors[rule.severity]}>
                    {rule.severity}
                  </Badge>
                  {!rule.enabled && (
                    <Badge variant="outline" className="text-slate-500">
                      Disabled
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600 mb-2">{typeInfo.label}</p>
                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                  <span>
                    Threshold: <strong>{rule.threshold_value}{rule.threshold_unit}</strong>
                  </span>
                  {rule.duration_minutes > 0 && (
                    <span>
                      Duration: <strong>{rule.duration_minutes} min</strong>
                    </span>
                  )}
                  {rule.service_name && (
                    <span>
                      Service: <strong>{rule.service_name}</strong>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Switch
                checked={rule.enabled}
                onCheckedChange={() => toggleRuleEnabled(rule)}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditingRule(rule)}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                onClick={() => handleDeleteRule(rule.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAlertForm = (rule, setRule, isEdit = false) => {
    const selectedType = getAlertTypeInfo(rule.alert_type);

    return (
      <div className="space-y-4">
        <div>
          <Label>Rule Name *</Label>
          <Input
            value={rule.name}
            onChange={(e) => setRule({ ...rule, name: e.target.value })}
            placeholder="e.g., Critical Disk Space Alert"
          />
        </div>

        <div>
          <Label>Alert Type *</Label>
          <Select value={rule.alert_type} onValueChange={(value) => {
            const typeInfo = getAlertTypeInfo(value);
            setRule({ 
              ...rule, 
              alert_type: value,
              threshold_unit: typeInfo.units[0] || '%',
              service_name: value === 'service_stopped' ? commonServices[0] : ''
            });
          }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {alertTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {rule.alert_type === 'service_stopped' ? (
          <div>
            <Label>Service Name *</Label>
            <Select value={rule.service_name} onValueChange={(value) => setRule({ ...rule, service_name: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select or type service name" />
              </SelectTrigger>
              <SelectContent>
                {commonServices.map(service => (
                  <SelectItem key={service} value={service}>
                    {service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-2"
              value={rule.service_name}
              onChange={(e) => setRule({ ...rule, service_name: e.target.value })}
              placeholder="Or type custom service name"
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Threshold Value *</Label>
              <Input
                type="number"
                value={rule.threshold_value}
                onChange={(e) => setRule({ ...rule, threshold_value: e.target.value })}
                placeholder="e.g., 10"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={rule.threshold_unit} onValueChange={(value) => setRule({ ...rule, threshold_unit: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectedType.units.map(unit => (
                    <SelectItem key={unit} value={unit}>
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {(rule.alert_type === 'cpu_usage' || rule.alert_type === 'ram_usage') && (
          <div>
            <Label>Duration (minutes)</Label>
            <Input
              type="number"
              value={rule.duration_minutes}
              onChange={(e) => setRule({ ...rule, duration_minutes: parseInt(e.target.value) || 0 })}
              placeholder="e.g., 5"
            />
            <p className="text-xs text-slate-500 mt-1">Alert only if condition persists for this duration</p>
          </div>
        )}

        <div>
          <Label>Severity Level *</Label>
          <Select value={rule.severity} onValueChange={(value) => setRule({ ...rule, severity: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <Label htmlFor="enabled-switch" className="cursor-pointer">Enable this rule</Label>
          <Switch
            id="enabled-switch"
            checked={rule.enabled}
            onCheckedChange={(checked) => setRule({ ...rule, enabled: checked })}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Asset Alert Configuration</h1>
        <p className="text-slate-600 mt-1">Configure global alert thresholds for all assets</p>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            General Alert Settings
          </CardTitle>
          <CardDescription>Configure how alerts are delivered and displayed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Alert Check Frequency</Label>
              <Select 
                value={generalSettings.alert_frequency_minutes.toString()} 
                onValueChange={(value) => setGeneralSettings({ ...generalSettings, alert_frequency_minutes: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">Every 5 minutes</SelectItem>
                  <SelectItem value="10">Every 10 minutes</SelectItem>
                  <SelectItem value="15">Every 15 minutes</SelectItem>
                  <SelectItem value="30">Every 30 minutes</SelectItem>
                  <SelectItem value="60">Every hour</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">How often to check for alert conditions</p>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-base">Alert Actions</Label>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-slate-600" />
                  <span className="text-sm">Create tickets automatically</span>
                </div>
                <Switch
                  checked={generalSettings.create_tickets}
                  onCheckedChange={(checked) => setGeneralSettings({ ...generalSettings, create_tickets: checked })}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-600" />
                  <span className="text-sm">Send email notifications</span>
                </div>
                <Switch
                  checked={generalSettings.send_emails}
                  onCheckedChange={(checked) => setGeneralSettings({ ...generalSettings, send_emails: checked })}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-slate-600" />
                  <span className="text-sm">Show in dashboard alerts</span>
                </div>
                <Switch
                  checked={generalSettings.show_dashboard}
                  onCheckedChange={(checked) => setGeneralSettings({ ...generalSettings, show_dashboard: checked })}
                />
              </div>
            </div>
          </div>

          <Button onClick={saveGeneralSettings} className="w-full sm:w-auto">
            <Save className="w-4 h-4 mr-2" />
            Save Settings
          </Button>
        </CardContent>
      </Card>

      {/* Alert Rules */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Alert Rules</CardTitle>
              <CardDescription>Manage threshold-based alert rules for your assets</CardDescription>
            </div>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading alert rules...</div>
          ) : alertRules.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 mb-4">No alert rules configured yet</p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Rule
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {alertRules.map(rule => renderRuleCard(rule))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Rule Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Alert Rule</DialogTitle>
            <DialogDescription>
              Define a new alert threshold that will apply to all assets
            </DialogDescription>
          </DialogHeader>
          {renderAlertForm(newRule, setNewRule)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRule}>Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Rule Dialog */}
      {editingRule && (
        <Dialog open={!!editingRule} onOpenChange={() => setEditingRule(null)}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Alert Rule</DialogTitle>
              <DialogDescription>
                Modify the alert threshold configuration
              </DialogDescription>
            </DialogHeader>
            {renderAlertForm(editingRule, setEditingRule, true)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingRule(null)}>Cancel</Button>
              <Button onClick={handleUpdateRule}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AssetAlertsSettings;
